import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Checkbox, Chip, Stack, TextField, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../generic/hooks/useDateFormatter'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import useMosInfo from '../common/provider/useMosInfo'
import MosSmartTable from '../common/MosSmartTable'
import MosSmartDialog from '../common/MosSmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import AlertUI from '../../../generic/AlertUI'
import WorkerRequiredButton from '../common/WorkerRequiredButton'
import { allowsPartialQuantity, allowsDecimalQuantity, normalizeInstanceQuantity } from './instanceGranularity'

const EMPTY_WASTE_DIALOG = { open: false, instanceIds: [] }
const EMPTY_RETURN_DIALOG = { open: false, instanceId: 0, available: 0 }

// #39 (Bloque D/S25): "loadedBy.type" tells apart a MOS worker load from a Raw Material Reference
// tab load (mutually exclusive, resolved backend-side). Only these two values are contracted.
const LOADED_BY_TYPE_LABEL = { WORKER: 'MOS', USER: 'Raw Material Reference' }

// S25: read-only "who loaded it" metadata — no loader recorded (legacy data, or after returning to
// general stock, S27) renders an empty cell; a loader whose name was lost (worker/user deleted)
// still shows a non-null placeholder.
const renderLoadedBy = (row, t_) => {
  const loadedBy = row.loadedBy || {}
  if (!loadedBy.id) return ''
  const name = loadedBy.name || t_('Unknown')
  const typeLabel = LOADED_BY_TYPE_LABEL[loadedBy.type] || ''
  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      <span>{name}</span>
      {typeLabel && <Chip size='small' variant='outlined' label={t_(typeLabel)} />}
    </Stack>
  )
}

// #39 (Bloque B): Return/waste require their own MOS capability — MACHINE_STORAGE_OPERATE for
// return (S28), MACHINE_STORAGE_WASTE for waste (S29, deliberately separate: holding OPERATE does
// NOT grant WASTE). Front gating only, the back verifies again fail-secure. `worker`-less state
// keeps both actions visible (same criterion as the pre-existing supervisor gating for the Active
// Jobs part actions) so WorkerRequiredButton can still prompt the presence flow first; once a
// worker is logged in, an empty `mosCapabilities` (S30 default) hides both.
export const MACHINE_STORAGE_OPERATE = 'MACHINE_STORAGE_OPERATE'
export const MACHINE_STORAGE_WASTE = 'MACHINE_STORAGE_WASTE'

// S2: drill of a single reference — lists only the instances AVAILABLE_IN_MACHINE for that
// reference, located in the Machine Storage of the current device (never general stock nor
// another machine).
// S28: returning an instance moves its remainder back to the general stock.
// S29: wasting an instance is a definitive write-off — the remainder does NOT go back to stock.
// S30: the waste reason is optional free text; the form must not require it.
// Multiselect: both endpoints already take an `instanceIds` array, so a leading checkbox column +
// a bulk actions bar let the operator return/waste several instances in a single call. Selection is
// tracked by instance id (survives pagination) and cleared after every successful action.
const InstancesDialog = ({ open, referenceId = 0, referenceCode = '', onClose, onChanged }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const { device, worker, hasCapability } = useMosInfo()
  const canOperate = worker ? (typeof hasCapability === 'function' && hasCapability(MACHINE_STORAGE_OPERATE)) : true
  const canWaste = worker ? (typeof hasCapability === 'function' && hasCapability(MACHINE_STORAGE_WASTE)) : true
  const [AlertElement, showAlert] = AlertUI()
  const [refreshKey, setRefreshKey] = useState(0)
  const [wasteDialog, setWasteDialog] = useState(EMPTY_WASTE_DIALOG)
  const [wasteReason, setWasteReason] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [pageInstances, setPageInstances] = useState([])
  // Reference granularity (fractionable/unitary) travels per instance row; same for all rows of the
  // reference. Drives whether a per-instance partial return is offered and if it is integer/decimal.
  const [flags, setFlags] = useState({})
  const [returnDialog, setReturnDialog] = useState(EMPTY_RETURN_DIALOG)
  const [returnQuantity, setReturnQuantity] = useState('')

  // The selection is meaningless across a different reference or a closed dialog.
  useEffect(() => {
    setSelectedIds([])
  }, [referenceId, open])

  // Paginated (Spring Page) — same RequestPagination contract as the content endpoint. The table's
  // page/size/filters/sort are forwarded to the endpoint and the Page is returned as-is so the
  // pageable table reads `content` + `totalElements`. Server-side: `code`/`batch` filter + sort,
  // `quantityAvailable` sorts only. The current page's rows are captured so the "select all" control
  // knows which instances are on screen.
  const fetchInstances = useCallback(async (requestPagination) => {
    if (!device?.id || !referenceId) {
      setPageInstances([])
      return { content: [], totalElements: 0 }
    }
    const result = await apiGet('mos/machineStorage/' + device.id + '/reference/' + referenceId + '/instances', requestPagination)
    const content = Array.isArray(result) ? result : (result?.content ?? [])
    setPageInstances(content)
    if (content.length > 0) setFlags({ fractionable: content[0].fractionable, unitary: content[0].unitary })
    return result
  }, [device?.id, referenceId])

  const partial = allowsPartialQuantity(flags)
  const allowDecimal = allowsDecimalQuantity(flags)

  const notifyChanged = useCallback(() => {
    setRefreshKey((value) => value + 1)
    if (onChanged) onChanged()
  }, [onChanged])

  const toggleInstance = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((current) => current !== id) : [...prev, id]))
  }, [])

  const pageIds = useMemo(() => pageInstances.map((row) => row.id), [pageInstances])
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id))

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const everySelected = pageIds.length > 0 && pageIds.every((id) => prev.includes(id))
      if (everySelected) return prev.filter((id) => !pageIds.includes(id))
      return Array.from(new Set([...prev, ...pageIds]))
    })
  }, [pageIds])

  const postReturn = useCallback(async (instanceSelections) => {
    if (!instanceSelections || instanceSelections.length === 0) return
    try {
      await apiPost('mos/machineStorage/' + worker.id + '/' + device.id + '/return', { instanceSelections })
      showAlert({ severity: 'success', title: t_(instanceSelections.length > 1 ? 'Instances returned to general stock' : 'Instance returned to general stock') })
      setSelectedIds([])
      notifyChanged()
    } catch (error) {
      showAlert({ severity: 'error', title: t_(error.message) })
    }
  }, [device?.id, worker?.id, showAlert, t_, notifyChanged])

  // Whole-instance return: the bulk action, and the per-row action for references that do not allow
  // partial (case 1). No quantity => the backend returns the whole instance.
  const returnWholeInstances = useCallback((instanceIds) => {
    postReturn((instanceIds || []).map((instanceId) => ({ instanceId })))
  }, [postReturn])

  const openReturnDialog = useCallback((row) => {
    setReturnQuantity('')
    setReturnDialog({ open: true, instanceId: row.id, available: row.quantityAvailable ?? 0 })
  }, [])

  const closeReturnDialog = useCallback(() => {
    setReturnDialog(EMPTY_RETURN_DIALOG)
  }, [])

  // Partial per-instance return (cases 2/3/4): normalized + capped to the instance available.
  const confirmReturn = useCallback(() => {
    const quantity = normalizeInstanceQuantity(returnQuantity, { decimal: allowDecimal, available: returnDialog.available })
    if (quantity <= 0) {
      showAlert({ severity: 'warning', title: t_('Quantity must be greater than 0') })
      return
    }
    closeReturnDialog()
    postReturn([{ instanceId: returnDialog.instanceId, quantity }])
  }, [returnQuantity, allowDecimal, returnDialog.available, returnDialog.instanceId, showAlert, t_, closeReturnDialog, postReturn])

  const openWasteDialog = useCallback((instanceIds) => {
    if (!instanceIds || instanceIds.length === 0) return
    setWasteReason('')
    setWasteDialog({ open: true, instanceIds })
  }, [])

  const closeWasteDialog = useCallback(() => {
    setWasteDialog(EMPTY_WASTE_DIALOG)
  }, [])

  const confirmWaste = useCallback(async () => {
    const instanceIds = wasteDialog.instanceIds
    try {
      await apiPost('mos/machineStorage/' + worker.id + '/' + device.id + '/waste', { instanceIds, reason: wasteReason })
      closeWasteDialog()
      showAlert({ severity: 'success', title: t_(instanceIds.length > 1 ? 'Instances wasted' : 'Instance wasted') })
      setSelectedIds([])
      notifyChanged()
    } catch (error) {
      showAlert({ severity: 'error', title: t_(error.message) })
    }
  }, [device?.id, worker?.id, wasteDialog.instanceIds, wasteReason, showAlert, t_, notifyChanged, closeWasteDialog])

  const columns = useMemo(() => {
    const list = []
    if (canOperate || canWaste) {
      list.push({
        name: '',
        field: 'selection',
        readOnly: true,
        search: false,
        sortable: false,
        render: (row) => (
          <Checkbox
            size='small'
            checked={selectedIds.includes(row.id)}
            onChange={() => toggleInstance(row.id)}
            inputProps={{ 'aria-label': t_('Select instance') }}
          />
        )
      })
    }
    list.push(
      { name: t_('Code'), field: 'code', readOnly: true },
      { name: t_('Batch'), field: 'batch', readOnly: true },
      { name: t_('Available in machine'), field: 'quantityAvailable', readOnly: true, search: false },
      {
        name: t_('Loaded by'),
        field: 'loadedBy',
        readOnly: true,
        search: false,
        sortable: false,
        render: (row) => renderLoadedBy(row, t_)
      },
      {
        name: t_('Loaded date'),
        field: 'loadedDate',
        readOnly: true,
        search: false,
        sortable: false,
        render: (row) => formatDateTime(row.loadedDate)
      },
      {
        name: t_('Actions'),
        field: 'actions',
        readOnly: true,
        search: false,
        sortable: false,
        render: (row) => (
          <Stack direction='row' spacing={1}>
            {canOperate && (
              <WorkerRequiredButton size='small' variant='outlined' onClick={() => (partial ? openReturnDialog(row) : returnWholeInstances([row.id]))}>
                {t_('Return')}
              </WorkerRequiredButton>
            )}
            {canWaste && (
              <WorkerRequiredButton size='small' variant='outlined' color='error' onClick={() => openWasteDialog([row.id])}>
                {t_('Waste')}
              </WorkerRequiredButton>
            )}
          </Stack>
        )
      }
    )
    return list
  }, [t_, formatDateTime, partial, openReturnDialog, returnWholeInstances, openWasteDialog, toggleInstance, selectedIds, canOperate, canWaste])

  const hasSelection = selectedIds.length > 0

  return (
    <>
      <MosSmartDialog
        title={createDialogTitle({ color: 'primary', title: t_('Instances of {{reference}}', { reference: referenceCode }) })}
        setOpen={open}
        accept={false}
        cancel={false}
        close
        closeCallback={onClose}
      >
        {AlertElement}
        {(canOperate || canWaste) && (
          <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
            <Checkbox
              size='small'
              checked={allPageSelected}
              indeterminate={!allPageSelected && somePageSelected}
              disabled={pageIds.length === 0}
              onChange={toggleSelectAll}
              inputProps={{ 'aria-label': t_('Select all') }}
            />
            <Typography variant='body2' color='text.secondary'>{t_('Select all')}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            {hasSelection && (
              <>
                <Typography variant='body2' color='text.secondary'>{selectedIds.length + ' ' + t_('items selected')}</Typography>
                {canOperate && (
                  <WorkerRequiredButton size='small' variant='outlined' onClick={() => returnWholeInstances(selectedIds)}>
                    {t_('Return')}
                  </WorkerRequiredButton>
                )}
                {canWaste && (
                  <WorkerRequiredButton size='small' variant='outlined' color='error' onClick={() => openWasteDialog(selectedIds)}>
                    {t_('Waste')}
                  </WorkerRequiredButton>
                )}
              </>
            )}
          </Stack>
        )}
        <MosSmartTable columns={columns} dataFetch={fetchInstances} pageable tablePageSize={10} forceLoad={refreshKey} showSearchHeader hideExport />
      </MosSmartDialog>
      <MosSmartDialog
        title={createDialogTitle({ color: 'error', title: t_(wasteDialog.instanceIds.length > 1 ? 'Waste instances' : 'Waste instance') })}
        setOpen={wasteDialog.open}
        cancelCallback={closeWasteDialog}
        acceptCallback={confirmWaste}
      >
        <TextField
          fullWidth
          multiline
          rows={3}
          value={wasteReason}
          label={t_('Waste reason (optional)')}
          onChange={(e) => setWasteReason(e.target.value)}
          size='small'
          sx={{ mt: 1 }}
        />
      </MosSmartDialog>
      <MosSmartDialog
        title={createDialogTitle({ color: 'primary', title: t_('Return to general stock') })}
        setOpen={returnDialog.open}
        cancelCallback={closeReturnDialog}
        acceptCallback={confirmReturn}
      >
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography variant='body2' color='text.secondary'>{t_('Available in machine') + ': ' + returnDialog.available}</Typography>
          <TextField
            fullWidth
            type='number'
            value={returnQuantity}
            label={t_('Quantity to return')}
            onChange={(e) => setReturnQuantity(e.target.value)}
            size='small'
            inputProps={{ min: 0, max: returnDialog.available, step: allowDecimal ? 'any' : 1 }}
          />
        </Stack>
      </MosSmartDialog>
    </>
  )
}

export default InstancesDialog
